import { Card } from '@salonomy/ui';
import { useAuth } from '../../lib/auth.js';
import { BookGdprControls } from './BookGdprControls.js';
import { ClientGdprControls } from './ClientGdprControls.js';

/**
 * The Settings "Privacy & data" section (WS6 brief item 5). Replaces the "Privacy & GDPR" ComingSoon
 * scaffold in SettingsPage. Composes the two GDPR surfaces and gates each one to mirror the API's
 * RBAC exactly — a role-escalation guard is shown as a disabled state with an explanation, never as
 * a server 403.
 *
 * RBAC (mirror of apps/api/src/modules/{gdpr,clients}):
 *   - Per-client export/erasure: manager-only (owner/manager). Hidden for everyone else.
 *   - Whole-book export: any session — renters get their sealed book, managers get the shared book.
 *   - Whole-book erasure: chair renters ONLY (the server rejects it for anyone without a sealed
 *     book). The book control disables the erase button for managers and explains why.
 *
 * A stylist/receptionist with no sealed book and no manager rank sees an honest "nothing to manage
 * here" note rather than a blank or a set of buttons that would only 403.
 */
export function GdprPanel() {
  const { user, isManager } = useAuth();
  const isRenter = user?.renterId != null;

  return (
    <Card>
      <div className="section-title">Privacy &amp; data</div>
      <div className="muted" style={{ fontSize: 13, margin: '4px 0 16px' }}>
        Data export and erasure under UK GDPR. Exports give a person a machine-readable copy of
        their record; erasure deletes it for good. Erasure runs online only — it is never queued
        offline, and it tells you the moment it has completed.
      </div>

      {isManager ? (
        <ClientGdprControls ownRenterId={user?.renterId ?? null} />
      ) : isRenter ? (
        <div className="muted" style={{ fontSize: 13 }}>
          Your clients are sealed to your book. Use the controls below to export or erase your whole
          book. Erasing or exporting an individual client is done by an owner or manager.
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 13 }}>
          Only an owner or manager can export or erase an individual client’s record.
        </div>
      )}

      {/* Whole-book controls: renters see their sealed book; managers see the shared book.
          A plain stylist/reception with no sealed book has no whole-book surface at all. */}
      {isRenter || isManager ? (
        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: '1px solid var(--slm-border, #E3E6ED)',
          }}
        >
          {/* The confirm-phrase for whole-book erasure is the renter's own display name —
              "type your name to erase your book". Managers can't erase the shared book, so the
              phrase is unused for them. */}
          <BookGdprControls isRenter={isRenter} bookLabel={user?.displayName ?? ''} />
        </div>
      ) : null}
    </Card>
  );
}
